/**
 * Single source of truth for role-based navigation, accent colors, and
 * primary CTAs. Imported by Navigation.js, LandingPage.js, RolePortal.js,
 * AuthPage.js and the role welcome card.
 *
 * Adding a 6th role? Append a new entry here — both menus will pick it up.
 */
import {
  House, Package, MagnifyingGlass, ClockCounterClockwise, Trophy, Car,
  MapTrifold, Bluetooth, CurrencyEur, AirplaneTilt, ChartBar, ShieldCheck,
  Crown, User, Storefront, Truck, Plugs,
} from "@phosphor-icons/react";

// Public/demo/anon nav (no logged-in user)
export const PUBLIC_ITEMS = [
  { id: "landing",   labelKey: "nav.home",     icon: House },
  { id: "routes",    labelKey: "nav.routes",   icon: AirplaneTilt },
  { id: "user",      labelKey: "nav.send",     icon: Package },
  { id: "tracking",  labelKey: "nav.track",    icon: MagnifyingGlass },
  { id: "fleet",     labelKey: "nav.fleet",    icon: Car },
  { id: "map",       labelKey: "nav.map",      icon: MapTrifold },
];

// Per-role config: visual color, portal label, demo creds, allowed views,
// menu items (post-login), home view, and dynamic hero CTA.
export const ROLES = {
  user: {
    color: "#2D4A3E",
    icon: User,
    labelKey: "portal.user_title",
    descKey: "portal.user_desc",
    demo: { email: "user@blumove.pt", password: "User2026!" },
    home: "user",
    items: [
      { id: "landing",       labelKey: "nav.home",          icon: House },
      { id: "user",          labelKey: "nav.send",          icon: Package },
      { id: "tracking",      labelKey: "nav.track",         icon: MagnifyingGlass },
      { id: "my-shipments",  labelKey: "nav.my_shipments",  icon: ClockCounterClockwise },
      { id: "gamification",  labelKey: "nav.achievements",  icon: Trophy },
    ],
    hero: { view: "user", labelKey: "role_cta.user" },
  },
  driver: {
    color: "#f59e0b",
    icon: Truck,
    labelKey: "portal.driver_title",
    descKey: "portal.driver_desc",
    demo: { email: "driver@blumove.pt", password: "Driver2026!" },
    home: "driver",
    items: [
      { id: "landing",       labelKey: "nav.home",          icon: House },
      { id: "driver",        labelKey: "nav.driver",        icon: Truck },
      { id: "fleet",         labelKey: "nav.fleet",         icon: Car },
      { id: "map",           labelKey: "nav.map",           icon: MapTrifold },
      { id: "my-shipments",  labelKey: "nav.deliveries",    icon: ClockCounterClockwise },
      { id: "gamification",  labelKey: "nav.achievements",  icon: Trophy },
    ],
    hero: { view: "driver", labelKey: "role_cta.driver" },
  },
  intermediary: {
    color: "#0ea5e9",
    icon: Storefront,
    labelKey: "portal.intermediary_title",
    descKey: "portal.intermediary_desc",
    demo: { email: "intermediary@blumove.pt", password: "Inter2026!" },
    home: "beacons",
    items: [
      { id: "landing",       labelKey: "nav.home",          icon: House },
      { id: "map",           labelKey: "nav.map",           icon: MapTrifold },
      { id: "beacons",       labelKey: "nav.beacons",       icon: Bluetooth },
      { id: "financial",     labelKey: "nav.financial",     icon: CurrencyEur },
      { id: "tracking",      labelKey: "nav.track",         icon: MagnifyingGlass },
    ],
    hero: { view: "beacons", labelKey: "role_cta.intermediary" },
  },
  admin: {
    color: "#8b5cf6",
    icon: ShieldCheck,
    labelKey: "portal.admin_title",
    descKey: "portal.admin_desc",
    demo: { email: "admin@blumove.pt", password: "BluMove2026!" },
    home: "admin",
    items: null,           // null = full menu (built dynamically below)
    hero: { view: "admin", labelKey: "role_cta.admin" },
  },
  super_admin: {
    color: "#dc2626",
    icon: Crown,
    labelKey: "portal.super_admin_title",
    descKey: "portal.super_admin_desc",
    demo: { email: "superadmin@blumove.pt", password: "SuperAdmin2026!" },
    home: "admin",
    items: null,
    hero: { view: "admin", labelKey: "role_cta.super_admin" },
  },
};

// The full admin/super-admin menu (kept once here, not duplicated)
export const ADMIN_ITEMS = (role = "admin") => [
  { id: "landing",       labelKey: "nav.home",          icon: House },
  { id: "routes",        labelKey: "nav.routes",        icon: AirplaneTilt },
  { id: "user",          labelKey: "nav.send",          icon: Package },
  { id: "tracking",      labelKey: "nav.track",         icon: MagnifyingGlass },
  { id: "my-shipments",  labelKey: "nav.my_shipments",  icon: ClockCounterClockwise },
  { id: "fleet",         labelKey: "nav.fleet",         icon: Car },
  { id: "map",           labelKey: "nav.map",           icon: MapTrifold },
  { id: "financial",     labelKey: "nav.financial",     icon: CurrencyEur },
  { id: "beacons",       labelKey: "nav.beacons",       icon: Bluetooth },
  { id: "admin",         labelKey: role === "super_admin" ? "nav.super_admin" : "nav.admin",
                          icon: role === "super_admin" ? Crown : ShieldCheck },
  ...(role === "super_admin"
    ? [{ id: "integrations", labelKey: "nav.integrations", icon: Plugs }]
    : []),
];

// Convenience selectors
export function getRoleConfig(role) {
  return ROLES[role] || null;
}

export function getNavItems(role) {
  if (!role) return PUBLIC_ITEMS;
  const cfg = ROLES[role];
  if (!cfg) return PUBLIC_ITEMS;
  return cfg.items || ADMIN_ITEMS(role);
}

export function getAllowedViewIds(role) {
  if (!role) return null; // unauth: allow all (LandingPage gates)
  const cfg = ROLES[role];
  if (!cfg) return null;
  if (!cfg.items) return null; // admin/super_admin: allow all
  return new Set(cfg.items.map((i) => i.id));
}

// Plain ordered list for the RolePortal entry screen
export const ROLE_ORDER = ["user", "intermediary", "driver", "admin", "super_admin"];
